///
/// EarthBrowser
///
/// Created by Matt Giger on December 16, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { logger } from '@/utils/LogDispatcher'
import type { StoredHandle, StoredFileHandle, StoredDirectoryHandle, ExternalGeoPackageFile } from './types'

/**
 * IndexedDB storage for File System Access API handles and GeoPackage cache
 *
 * Uses separate databases for persistent vs. temporary data:
 * - EarthBrowserHandles: Persistent File System Access API handles
 * - EarthBrowserCache: Temporary cached data (can be cleared anytime)
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
export class FileSystemHandleStore {
	// Persistent database for File System Access API handles
	private static readonly HANDLES_DB_NAME = 'EarthBrowserHandles'
	private static readonly HANDLES_DB_VERSION = 1
	private static readonly HANDLES_STORE = 'handles'

	// Temporary/cache database for GeoPackage metadata
	private static readonly CACHE_DB_NAME = 'EarthBrowserCache'
	private static readonly CACHE_DB_VERSION = 1
	private static readonly GEOPACKAGES_STORE = 'geopackages'

	// Legacy database name for migration
	private static readonly LEGACY_DB_NAME = 'EarthBrowserFileSystem'

	private handlesDb: IDBDatabase | null = null
	private cacheDb: IDBDatabase | null = null
	private migrated = false

	/**
	 * Initialize the IndexedDB databases
	 */
	async initialize(): Promise<void> {
		// Initialize persistent handles database
		await this.initializeHandlesDb()

		// Initialize temporary cache database
		await this.initializeCacheDb()

		// Check for and migrate legacy database
		await this.migrateLegacyDatabase()
	}

	/**
	 * Initialize persistent handles database
	 */
	private async initializeHandlesDb(): Promise<void> {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(FileSystemHandleStore.HANDLES_DB_NAME, FileSystemHandleStore.HANDLES_DB_VERSION)

			request.onerror = () => {
				logger.error('Failed to open Handles IndexedDB', request.error)
				reject(request.error)
			}

			request.onsuccess = () => {
				this.handlesDb = request.result
				resolve()
			}

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result

				// Create handles store
				if (!db.objectStoreNames.contains(FileSystemHandleStore.HANDLES_STORE)) {
					const handleStore = db.createObjectStore(FileSystemHandleStore.HANDLES_STORE, { keyPath: 'id' })
					handleStore.createIndex('kind', 'kind', { unique: false })
					handleStore.createIndex('name', 'name', { unique: false })
					handleStore.createIndex('lastAccessed', 'lastAccessed', { unique: false })
					logger.info('Handles store created')
				}
			}
		})
	}

	/**
	 * Initialize temporary cache database
	 */
	private async initializeCacheDb(): Promise<void> {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(FileSystemHandleStore.CACHE_DB_NAME, FileSystemHandleStore.CACHE_DB_VERSION)

			request.onerror = () => {
				logger.error('Failed to open Cache IndexedDB', request.error)
				reject(request.error)
			}

			request.onsuccess = () => {
				this.cacheDb = request.result
				resolve()
			}

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result

				// Create geopackages store
				if (!db.objectStoreNames.contains(FileSystemHandleStore.GEOPACKAGES_STORE)) {
					const gpkgStore = db.createObjectStore(FileSystemHandleStore.GEOPACKAGES_STORE, { keyPath: 'id' })
					gpkgStore.createIndex('handleId', 'handleId', { unique: false })
					gpkgStore.createIndex('lastOpened', 'lastOpened', { unique: false })
					logger.info('GeoPackages cache store created')
				}
			}
		})
	}

	/**
	 * Migrate data from legacy database to new separate databases
	 */
	private async migrateLegacyDatabase(): Promise<void> {
		if (this.migrated) return

		try {
			// Check if legacy database exists
			const databases = await indexedDB.databases()
			const legacyDb = databases.find(db => db.name === FileSystemHandleStore.LEGACY_DB_NAME)

			if (!legacyDb) {
				this.migrated = true
				return // No legacy database to migrate
			}

			logger.info('Legacy database detected, starting migration...')

			// Open legacy database
			const db = await new Promise<IDBDatabase>((resolve, reject) => {
				const request = indexedDB.open(FileSystemHandleStore.LEGACY_DB_NAME)
				request.onsuccess = () => resolve(request.result)
				request.onerror = () => reject(request.error)
			})

			// Migrate handles if the store exists
			if (db.objectStoreNames.contains(FileSystemHandleStore.HANDLES_STORE)) {
				const transaction = db.transaction(FileSystemHandleStore.HANDLES_STORE, 'readonly')
				const store = transaction.objectStore(FileSystemHandleStore.HANDLES_STORE)
				const handles = await new Promise<StoredHandle[]>((resolve) => {
					const request = store.getAll()
					request.onsuccess = () => resolve(request.result || [])
					request.onerror = () => resolve([])
				})

				// Store in new handles database
				for (const handle of handles) {
					await this.storeHandle(handle)
				}
				logger.info(`Migrated ${handles.length} handles to new database`)
			}

			// Migrate geopackages if the store exists
			if (db.objectStoreNames.contains(FileSystemHandleStore.GEOPACKAGES_STORE)) {
				const transaction = db.transaction(FileSystemHandleStore.GEOPACKAGES_STORE, 'readonly')
				const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
				const geopackages = await new Promise<ExternalGeoPackageFile[]>((resolve) => {
					const request = store.getAll()
					request.onsuccess = () => resolve(request.result || [])
					request.onerror = () => resolve([])
				})

				// Store in new cache database
				for (const gpkg of geopackages) {
					await this.storeGeoPackage(gpkg)
				}
				logger.info(`Migrated ${geopackages.length} geopackages to cache database`)
			}

			db.close()

			// Delete legacy database
			await new Promise<void>((resolve, _reject) => {
				const request = indexedDB.deleteDatabase(FileSystemHandleStore.LEGACY_DB_NAME)
				request.onsuccess = () => {
					logger.info('Legacy database deleted successfully')
					resolve()
				}
				request.onerror = () => {
					logger.warn('Failed to delete legacy database, but migration completed')
					resolve() // Don't fail if we can't delete
				}
			})

			this.migrated = true
			logger.info('Database migration completed successfully')
		} catch (error) {
			logger.error('Failed to migrate legacy database:', [[error]])
			// Don't throw - allow app to continue with new databases
			this.migrated = true
		}
	}

	/**
	 * Store a file system handle
	 */
	async storeHandle(handle: StoredHandle): Promise<void> {
		if (!this.handlesDb) throw new Error('Handles database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.handlesDb!.transaction([FileSystemHandleStore.HANDLES_STORE], 'readwrite')
			const store = transaction.objectStore(FileSystemHandleStore.HANDLES_STORE)
			const request = store.put(handle)

			request.onsuccess = () => {
				logger.info(`Stored file system handle: ${handle.name} (${handle.id})`)
				resolve()
			}

			request.onerror = () => {
				logger.error('Failed to store handle', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Get a stored handle by ID
	 */
	async getHandle(id: string): Promise<StoredHandle | null> {
		if (!this.handlesDb) throw new Error('Handles database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.handlesDb!.transaction([FileSystemHandleStore.HANDLES_STORE], 'readonly')
			const store = transaction.objectStore(FileSystemHandleStore.HANDLES_STORE)
			const request = store.get(id)

			request.onsuccess = () => {
				resolve(request.result || null)
			}

			request.onerror = () => {
				logger.error('Failed to get handle', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Get all stored handles
	 */
	async getAllHandles(): Promise<StoredHandle[]> {
		if (!this.handlesDb) throw new Error('Handles database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.handlesDb!.transaction([FileSystemHandleStore.HANDLES_STORE], 'readonly')
			const store = transaction.objectStore(FileSystemHandleStore.HANDLES_STORE)
			const request = store.getAll()

			request.onsuccess = () => {
				resolve(request.result || [])
			}

			request.onerror = () => {
				logger.error('Failed to get all handles', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Get all file handles
	 */
	async getFileHandles(): Promise<StoredFileHandle[]> {
		const handles = await this.getAllHandles()
		return handles.filter(h => h.kind === 'file')
	}

	/**
	 * Get all directory handles
	 */
	async getDirectoryHandles(): Promise<StoredDirectoryHandle[]> {
		const handles = await this.getAllHandles()
		return handles.filter(h => h.kind === 'directory')
	}

	/**
	 * Delete a stored handle
	 */
	async deleteHandle(id: string): Promise<void> {
		if (!this.handlesDb) throw new Error('Handles database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.handlesDb!.transaction([FileSystemHandleStore.HANDLES_STORE], 'readwrite')
			const store = transaction.objectStore(FileSystemHandleStore.HANDLES_STORE)
			const request = store.delete(id)

			request.onsuccess = () => {
				logger.info(`Deleted file system handle: ${id}`)
				resolve()
			}

			request.onerror = () => {
				logger.error('Failed to delete handle', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Update handle's last accessed time
	 */
	async updateLastAccessed(id: string): Promise<void> {
		const handle = await this.getHandle(id)
		if (handle) {
			handle.lastAccessed = Date.now()
			await this.storeHandle(handle)
		}
	}

	/**
	 * Store a GeoPackage file reference
	 */
	async storeGeoPackage(geopackage: ExternalGeoPackageFile): Promise<void> {
		if (!this.cacheDb) throw new Error('Cache database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.cacheDb!.transaction([FileSystemHandleStore.GEOPACKAGES_STORE], 'readwrite')
			const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
			const request = store.put(geopackage)

			request.onsuccess = () => {
				logger.info(`Stored GeoPackage reference: ${geopackage.name} (${geopackage.id})`)
				resolve()
			}

			request.onerror = () => {
				logger.error('Failed to store GeoPackage', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Get a GeoPackage reference by ID
	 */
	async getGeoPackage(id: string): Promise<ExternalGeoPackageFile | null> {
		if (!this.cacheDb) throw new Error('Cache database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.cacheDb!.transaction([FileSystemHandleStore.GEOPACKAGES_STORE], 'readonly')
			const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
			const request = store.get(id)

			request.onsuccess = () => {
				resolve(request.result || null)
			}

			request.onerror = () => {
				logger.error('Failed to get GeoPackage', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Get all stored GeoPackage references
	 */
	async getAllGeoPackages(): Promise<ExternalGeoPackageFile[]> {
		if (!this.cacheDb) throw new Error('Cache database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.cacheDb!.transaction([FileSystemHandleStore.GEOPACKAGES_STORE], 'readonly')
			const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
			const request = store.getAll()

			request.onsuccess = () => {
				resolve(request.result || [])
			}

			request.onerror = () => {
				logger.error('Failed to get all GeoPackages', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Get GeoPackages by handle ID
	 */
	async getGeoPackagesByHandle(handleId: string): Promise<ExternalGeoPackageFile[]> {
		if (!this.cacheDb) throw new Error('Cache database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.cacheDb!.transaction([FileSystemHandleStore.GEOPACKAGES_STORE], 'readonly')
			const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
			const index = store.index('handleId')
			const request = index.getAll(handleId)

			request.onsuccess = () => {
				resolve(request.result || [])
			}

			request.onerror = () => {
				logger.error('Failed to get GeoPackages by handle', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Delete a GeoPackage reference
	 */
	async deleteGeoPackage(id: string): Promise<void> {
		if (!this.cacheDb) throw new Error('Cache database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.cacheDb!.transaction([FileSystemHandleStore.GEOPACKAGES_STORE], 'readwrite')
			const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
			const request = store.delete(id)

			request.onsuccess = () => {
				logger.info(`Deleted GeoPackage reference: ${id}`)
				resolve()
			}

			request.onerror = () => {
				logger.error('Failed to delete GeoPackage', request.error)
				reject(request.error)
			}
		})
	}

	/**
	 * Update GeoPackage's last opened time
	 */
	async updateGeoPackageLastOpened(id: string): Promise<void> {
		const geopackage = await this.getGeoPackage(id)
		if (geopackage) {
			geopackage.lastOpened = Date.now()
			await this.storeGeoPackage(geopackage)
		}
	}

	/**
	 * Close the database connections
	 */
	close(): void {
		if (this.handlesDb) {
			this.handlesDb.close()
			this.handlesDb = null
			logger.info('Handles IndexedDB closed')
		}
		if (this.cacheDb) {
			this.cacheDb.close()
			this.cacheDb = null
			logger.info('Cache IndexedDB closed')
		}
	}

	/**
	 * Clear all cache data (GeoPackages)
	 * Leaves handles database intact
	 */
	async clearCache(): Promise<void> {
		if (!this.cacheDb) throw new Error('Cache database not initialized')

		return new Promise((resolve, reject) => {
			const transaction = this.cacheDb!.transaction([FileSystemHandleStore.GEOPACKAGES_STORE], 'readwrite')
			const store = transaction.objectStore(FileSystemHandleStore.GEOPACKAGES_STORE)
			const request = store.clear()

			request.onsuccess = () => {
				logger.info('Cache cleared successfully')
				resolve()
			}

			request.onerror = () => {
				logger.error('Failed to clear cache', request.error)
				reject(request.error)
			}
		})
	}
}

